import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
  Tooltip,
  Alert,
  Snackbar,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DescriptionIcon from '@mui/icons-material/Description';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PeopleIcon from '@mui/icons-material/People';
import InfoIcon from '@mui/icons-material/Info';
import BulkUser from './BulkUser';

const LabList = ({ ...rest }) => {
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  // Download Bulk User template
  const handleClickDownload = () => {
    try {
      const link = document.createElement('a');
      link.href = 'https://labasservice-bulkusermgmt.s3.ap-south-1.amazonaws.com/BulkUsers.xlsx';
      link.download = 'BulkUser.xlsx';
      link.click();
      setDownloadSuccess(true);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleCloseSnackbar = () => {
    setDownloadSuccess(false);
  };

  return (
    <Container maxWidth={false} sx={{ py: 6, pb: 8, px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 } }}>
      <Card {...rest} sx={{ mb: 4 }}>
        {/* Hero Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, rgba(46,125,50,0.12), rgba(21,101,192,0.12))',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            p: { xs: 2.5, md: 3 },
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <PeopleIcon color="primary" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h4" component="h1">
                Bulk User Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Download the template, fill in user details, and upload to manage users in bulk
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Divider />

        {/* Instructions */}
        <Box sx={{ p: { xs: 2.5, md: 3 }, pb: 0 }}>
          <Alert 
            icon={<InfoIcon />} 
            severity="info" 
            sx={{ mb: 3 }}
          >
            <Typography variant="body2" component="div">
              <strong>How to use:</strong>
              <ol style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                <li>Download the Bulk User Operation Template</li>
                <li>Fill in the user details in the Excel file</li>
                <li>Upload the completed file using the upload section</li>
              </ol>
            </Typography>
          </Alert>
        </Box>

        {/* Main Content */}
        <Box sx={{ p: { xs: 2.5, md: 3 } }}>
          <Grid
            container
            spacing={{ xs: 3, sm: 4 }}
            sx={{ alignItems: 'stretch' }}
          >
            {/* Download Template Card */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                  },
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                  <Stack spacing={2} alignItems="center" sx={{ flexGrow: 1 }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: '50%',
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <DescriptionIcon sx={{ fontSize: 40 }} />
                    </Box>
                    <Typography variant="h5" align="center" sx={{ fontWeight: 600 }}>
                      Download Template
                    </Typography>
                    <Typography 
                      variant="body2" 
                      align="center" 
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Download the Bulk User Operation template to get started with bulk user management
                    </Typography>
                    <Box sx={{ mt: 'auto', pt: 2 }}>
                      <Tooltip title="Download Bulk User Template" arrow>
                        <Button
                          variant="contained"
                          onClick={handleClickDownload}
                          startIcon={<CloudDownloadIcon />}
                          sx={{
                            minWidth: 180,
                            borderRadius: 2,
                            px: 3,
                            py: 1.5,
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            boxShadow: 2,
                            '&:hover': {
                              boxShadow: 4,
                            },
                          }}
                          aria-label="Download Bulk User Template"
                        >
                          Download Template
                        </Button>
                      </Tooltip>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Upload Card */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                  },
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                  <Stack spacing={2} alignItems="center" sx={{ flexGrow: 1 }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: '50%',
                        backgroundColor: 'info.light',
                        color: 'info.contrastText',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CloudUploadIcon sx={{ fontSize: 40 }} />
                    </Box>
                    <Typography variant="h5" align="center" sx={{ fontWeight: 600 }}>
                      Upload Users
                    </Typography>
                    <Typography 
                      variant="body2" 
                      align="center" 
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Upload your completed Bulk User Operation file to process user management operations
                    </Typography>
                    <Box sx={{ mt: 'auto', pt: 2, width: '100%', display: 'flex', justifyContent: 'center' }}>
                      <BulkUser />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Card>

      {/* Success Snackbar */}
      <Snackbar
        open={downloadSuccess}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Template downloaded successfully!
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LabList;
